import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";

export interface SystemSettingsData {
  id: string;
  max_failed_login_attempts: number;
  lockout_duration_minutes: number;
  password_min_length: number;
  password_require_uppercase: boolean;
  password_require_lowercase: boolean;
  password_require_number: boolean;
  password_require_special: boolean;
  password_history_count: number;
  mfa_enforced: boolean;
  mfa_remember_days: number;
  mfa_app_name: string | null;
  email_sender_email: string | null;
  email_sender_name: string | null;
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_user: string | null;
  password_reset_token_expiry: number;
}

const QUERY_KEY = ["system-settings"] as const;

export const useSystemSettings = () => {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<SystemSettingsData> => {
      const { data } = await apiClient.get(API_ENDPOINTS.SYSTEM.SETTINGS);
      return data;
    },
    staleTime: 60 * 1000,
  });
};

export const useUpdateSystemSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Omit<SystemSettingsData, "id">>) => {
      const { data } = await apiClient.patch(API_ENDPOINTS.SYSTEM.SETTINGS, payload);
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });
};
